import {Suspense, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css as emotionCss} from "@emotion/react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {IQueryValue} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {closeQuickViewHit, openQuickViewHit} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {getAmplitudePropertyListItem, IAmplitudePropertyListMainOffer} from "../../../tracking/amplitude/get_amplitude_property_list_item";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {AmplitudePropertyListView} from "../../../tracking/amplitude/track_amplitude_property_view_item_list";
import {ILatestQuery} from "../../../utils/latest_query";
import {useGetOfferDetailMapQuery} from "../../api/get_offer_detail";
import {OfferSidebar} from "../../atoms/OfferSidebar";
import {OfferSidebarBox} from "../../atoms/OfferSidebarBox";
import {OfferSidebarPropertiesList} from "../../atoms/OfferSidebarPropertiesList";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {getInvestmentApartmentsAmplitudeRequestKey} from "../../list/helpers/investment_apartments_amplitude_item_list_id/create_investment_apartments_amplitude_item_list_id";
import {getInvestmentApartmentsAmplitudeItemListName} from "../../list/helpers/investment_apartments_amplitude_item_list_id/investment_apartments_amplitude_item_list_name";
import {useInvestmentApartmentsAmplitudeItemListId} from "../../list/helpers/investment_apartments_amplitude_item_list_id/use_investment_apartments_amplitude_item_list_id";
import {
    floorChoiceOptions,
    houseStoreysOptions,
    mapArea,
    mapFromFilterOptions,
    mapRooms
} from "../../tracking/amplitude/amplitude_listing_filters_tracking_utils";
import {hitPropertyListViewChange} from "../../tracking/hit_property_list_view_change";
import {ExpandablePanel} from "../os_map_for_offers/atoms/ExpandablePanel";
import {IMapOfferPanelListQuery, MapOfferExpandablePanelOffer} from "./MapOfferExpandablePanelOffer";
import {MapOfferExpandablePanelQuickView} from "./MapOfferExpandablePanelQuickView";

interface IProps {
    offerId: number;
    onClose: () => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    isOpen: boolean;
    handleSetOfferId: (offerId: number | null) => void;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    offerIndex?: number;
}

const trackingMeta = {
    listId: "zobacz_na_mapie",
    listName: "Zobacz na mapie"
};

const amplitudePropertyListMainOffer = {
    facilities: null,
    parking_places: [],
    standard_description: {heating: null, types: []}
} as unknown as IAmplitudePropertyListMainOffer;

export const MapOfferPreview = (props: IProps) => {
    const {offerId, onClose, latestQuery, mapOfferListQuery, isOpen, handleSetOfferId, amplitudeItemListContext, offerIndex = 0} = props;

    const {data: offer} = useGetOfferDetailMapQuery(offerId);

    const {isMobile} = useUserDevice();
    const dispatch = useDispatch();

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, latestQuery);
    const searchFiltersToQuery = toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offerId, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE, searchFiltersToQuery);
    const {requestState} = propertyList;

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [currentPropertyListType, setCurrentPropertyListType] = useState<PropertyListType>(PropertyListType.TILES);
    const [panelListQuery, setPanelListQuery] = useState<IMapOfferPanelListQuery | null>(null);

    const {properties} = propertyList;
    const activePage = panelListQuery?.page ?? propertyList.page.currentPage;
    const activeSort = panelListQuery?.sort ?? propertyList.sort.currentSort;
    const activeFilters = panelListQuery?.filters ?? propertyList.filters.currentFilters;
    const listView: AmplitudePropertyListView = currentPropertyListType === PropertyListType.TILES ? "floor_plans" : "list";
    const arePropertiesOfSelectedOffer = properties.length === 0 || properties[0].offer.id === offerId;
    const isPropertyListReady = isOpen && requestState === RequestState.Success && arePropertiesOfSelectedOffer;

    const propertiesAmplitudeItemListId = useInvestmentApartmentsAmplitudeItemListId({
        offerId,
        page: activePage,
        sort: activeSort,
        filters: activeFilters,
        isResponseReady: isPropertyListReady
    });

    const propertiesAmplitudeItemListName = offer ? getInvestmentApartmentsAmplitudeItemListName({offerType: offer.type}) : null;

    const propertiesAmplitudeItemListContext = useMemo<IAmplitudeItemListContext | null>(
        () =>
            propertiesAmplitudeItemListId && propertiesAmplitudeItemListName
                ? {itemListId: propertiesAmplitudeItemListId, itemListName: propertiesAmplitudeItemListName}
                : null,
        [propertiesAmplitudeItemListId, propertiesAmplitudeItemListName]
    );

    const handleQuickViewOpen = (id: number) => {
        setSelectedIndex(id);
        openQuickViewHit(offerId, propertyList.properties[id].id);
    };

    const handleQuickViewClose = (): void => {
        setSelectedIndex(null);
        closeQuickViewHit();
    };

    const handleListTypeChange = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");

        if (type !== currentPropertyListType) {
            trackAmplitudeButtonClick({
                buttonName: "offers_view_toggle",
                buttonCategory: "listing_map",
                value: type === PropertyListType.TILES ? "floor_plans" : "list",
                itemId: offerId,
                propertyId: null
            });
        }

        setCurrentPropertyListType(type);
    };

    useEffect(() => {
        setPanelListQuery(null);
    }, [offerId]);

    const activeFiltersKey = useMemo(() => JSON.stringify(activeFilters), [activeFilters]);
    const trackedFiltersRef = useRef<{offerId: number; filtersKey: string} | null>(null);

    useEffect(() => {
        if (!offer || !isPropertyListReady || !propertiesAmplitudeItemListId) {
            return;
        }

        const trackedFilters = trackedFiltersRef.current;
        const isSameInvestment = trackedFilters?.offerId === offer.id;

        if (isSameInvestment && trackedFilters?.filtersKey === activeFiltersKey) {
            return;
        }

        trackedFiltersRef.current = {offerId: offer.id, filtersKey: activeFiltersKey};

        trackAmplitudeEvent("listing_filters_applied", {
            filter_trigger: isSameInvestment ? "manual" : "automatic",
            location: "listing_map",
            item_id: offer.id,
            property_type: null,
            budget: null,
            ...getAmplitudePanelFilterValues(activeFilters),
            completion_date: null,
            house_type: null,
            house_additional_areas: null,
            house_storeys: null,
            item_list_id: propertiesAmplitudeItemListId
        });
    }, [offer, isPropertyListReady, propertiesAmplitudeItemListId, activeFilters, activeFiltersKey]);

    const trackedViewItemListKeyRef = useRef<string | null>(null);
    const amplitudeRequestKey = getInvestmentApartmentsAmplitudeRequestKey({offerId, page: activePage, sort: activeSort, filters: activeFilters});

    useEffect(() => {
        if (!offer || !isPropertyListReady || !propertiesAmplitudeItemListId || !propertiesAmplitudeItemListName || properties.length === 0) {
            return;
        }

        const trackedKey = `${amplitudeRequestKey}|${listView}`;

        if (trackedViewItemListKeyRef.current === trackedKey) {
            return;
        }

        trackedViewItemListKeyRef.current = trackedKey;

        trackAmplitudeEvent("view_item_list", {
            item_id: offer.id,
            item_list_id: propertiesAmplitudeItemListId,
            item_list_name: propertiesAmplitudeItemListName,
            location: "listing_map",
            list_view: listView,
            products: properties.map((property, index) => getAmplitudePropertyListItem({property, mainOffer: amplitudePropertyListMainOffer, index}))
        });
    }, [offer, isPropertyListReady, propertiesAmplitudeItemListId, propertiesAmplitudeItemListName, properties, listView, amplitudeRequestKey]);

    useEffect(() => {
        if (offer && isOpen) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1, ...(propertyList.sort.currentSort ? {sort: propertyList.sort.currentSort} : {})},
                searchFiltersToQuery
            )(dispatch);
            propertyList.page.setCurrentPage(1);
        }
    }, [offer, isOpen]);

    return (
        <>
            {!isMobile && (
                <OfferSidebar animation={isOpen} css={mapOfferSidebarStyle}>
                    {isOpen && offer && (
                        <Suspense fallback={<Loader />}>
                            <div className={sidebarContentWrapperStyle}>
                                <OfferSidebarBox
                                    offer={offer}
                                    onOfferSidebarClose={onClose}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                    offerIndex={offerIndex}
                                />
                                <OfferSidebarPropertiesList
                                    offer={offer}
                                    propertyList={propertyList}
                                    propertyListType={currentPropertyListType}
                                    trackingMeta={trackingMeta}
                                    onPropertyListTypeClick={handleListTypeChange}
                                    amplitudeItemListContext={propertiesAmplitudeItemListContext}
                                />
                            </div>
                        </Suspense>
                    )}
                </OfferSidebar>
            )}

            {isMobile && offer && (
                <ExpandablePanel handleClose={onClose} isOpen={isOpen} className={panelStyle}>
                    <Suspense fallback={<Loader />}>
                        {selectedIndex === null && (
                            <MapOfferExpandablePanelOffer
                                offer={offer}
                                handleSetOfferId={handleSetOfferId}
                                latestQuery={latestQuery}
                                mapOfferListQuery={mapOfferListQuery}
                                handleQuickViewOpen={handleQuickViewOpen}
                                propertyListType={currentPropertyListType}
                                onPropertyListTypeClick={handleListTypeChange}
                                amplitudeItemListContext={amplitudeItemListContext}
                                propertiesAmplitudeItemListContext={propertiesAmplitudeItemListContext}
                                onListQueryChange={setPanelListQuery}
                                offerIndex={offerIndex}
                            />
                        )}

                        {selectedIndex !== null && (
                            <MapOfferExpandablePanelQuickView
                                handleQuickViewClose={handleQuickViewClose}
                                propertyList={propertyList}
                                selectedIndex={selectedIndex}
                                setSelectedIndex={setSelectedIndex}
                                propertiesRequestState={requestState}
                                offer={offer}
                                amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            />
                        )}
                    </Suspense>
                </ExpandablePanel>
            )}
        </>
    );
};

const getAmplitudePanelFilterValues = (filters: Record<string, IQueryValue>) => {
    const floorChoices = toAmplitudeFilterOptionValues(filters.floor_choices);
    const houseStoreys = toAmplitudeFilterOptionValues(filters.house_storeys);
    const floor = floorChoices.length ? mapFromFilterOptions(floorChoices, floorChoiceOptions) : mapFromFilterOptions(houseStoreys, houseStoreysOptions);

    return {
        rooms: mapRooms({lower: toAmplitudeFilterNumber(filters.rooms_0), upper: toAmplitudeFilterNumber(filters.rooms_1)}) ?? null,
        area: mapArea({lower: toAmplitudeFilterText(filters.area_0), upper: toAmplitudeFilterText(filters.area_1)}) ?? null,
        floor: floor ?? null
    };
};

const toAmplitudeFilterNumber = (value: IQueryValue): number | "" => (value == null || value === "" ? "" : Number(value));

const toAmplitudeFilterText = (value: IQueryValue): string => (value == null ? "" : String(value));

const toAmplitudeFilterOptionValues = (value: IQueryValue): string[] => {
    if (Array.isArray(value)) {
        return value.map(String);
    }

    return value == null || value === "" ? [] : [String(value)];
};

const panelStyle = css({
    width: "100%",
    padding: 0
});

const mapOfferSidebarStyle = emotionCss`
    @media (min-width: ${getThemeBreakpoint().md}) {
        padding: 0;
        width: max(70rem, 42%);
    }
`;

const sidebarContentWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    padding: 1.5,
    minHeight: "100%"
});
import {Suspense, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css as emotionCss} from "@emotion/react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {IQueryValue} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {closeQuickViewHit, openQuickViewHit} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {getAmplitudePropertyListItem, IAmplitudePropertyListMainOffer} from "../../../tracking/amplitude/get_amplitude_property_list_item";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {AmplitudePropertyListView} from "../../../tracking/amplitude/track_amplitude_property_view_item_list";
import {ILatestQuery} from "../../../utils/latest_query";
import {useGetOfferDetailMapQuery} from "../../api/get_offer_detail";
import {OfferSidebar} from "../../atoms/OfferSidebar";
import {OfferSidebarBox} from "../../atoms/OfferSidebarBox";
import {OfferSidebarPropertiesList} from "../../atoms/OfferSidebarPropertiesList";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {getInvestmentApartmentsAmplitudeRequestKey} from "../../list/helpers/investment_apartments_amplitude_item_list_id/create_investment_apartments_amplitude_item_list_id";
import {getInvestmentApartmentsAmplitudeItemListName} from "../../list/helpers/investment_apartments_amplitude_item_list_id/investment_apartments_amplitude_item_list_name";
import {useInvestmentApartmentsAmplitudeItemListId} from "../../list/helpers/investment_apartments_amplitude_item_list_id/use_investment_apartments_amplitude_item_list_id";
import {
    floorChoiceOptions,
    houseStoreysOptions,
    mapArea,
    mapFromFilterOptions,
    mapRooms
} from "../../tracking/amplitude/amplitude_listing_filters_tracking_utils";
import {hitPropertyListViewChange} from "../../tracking/hit_property_list_view_change";
import {ExpandablePanel} from "../os_map_for_offers/atoms/ExpandablePanel";
import {IMapOfferPanelListQuery, MapOfferExpandablePanelOffer} from "./MapOfferExpandablePanelOffer";
import {MapOfferExpandablePanelQuickView} from "./MapOfferExpandablePanelQuickView";

interface IProps {
    offerId: number;
    onClose: () => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    isOpen: boolean;
    handleSetOfferId: (offerId: number | null) => void;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    offerIndex?: number;
}

const trackingMeta = {
    listId: "zobacz_na_mapie",
    listName: "Zobacz na mapie"
};

const amplitudePropertyListMainOffer = {
    facilities: null,
    parking_places: [],
    standard_description: {heating: null, types: []}
} as unknown as IAmplitudePropertyListMainOffer;

export const MapOfferPreview = (props: IProps) => {
    const {offerId, onClose, latestQuery, mapOfferListQuery, isOpen, handleSetOfferId, amplitudeItemListContext, offerIndex = 0} = props;

    const {data: offer} = useGetOfferDetailMapQuery(offerId);

    const {isMobile} = useUserDevice();
    const dispatch = useDispatch();

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, latestQuery);
    const searchFiltersToQuery = toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offerId, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE, searchFiltersToQuery);
    const {requestState} = propertyList;

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [currentPropertyListType, setCurrentPropertyListType] = useState<PropertyListType>(PropertyListType.TILES);
    const [panelListQuery, setPanelListQuery] = useState<IMapOfferPanelListQuery | null>(null);

    const {properties} = propertyList;
    const activePage = panelListQuery?.page ?? propertyList.page.currentPage;
    const activeSort = panelListQuery?.sort ?? propertyList.sort.currentSort;
    const activeFilters = panelListQuery?.filters ?? propertyList.filters.currentFilters;
    const listView: AmplitudePropertyListView = currentPropertyListType === PropertyListType.TILES ? "floor_plans" : "list";
    const arePropertiesOfSelectedOffer = properties.length === 0 || properties[0].offer.id === offerId;
    const isPropertyListReady = isOpen && requestState === RequestState.Success && arePropertiesOfSelectedOffer;

    const propertiesAmplitudeItemListId = useInvestmentApartmentsAmplitudeItemListId({
        offerId,
        page: activePage,
        sort: activeSort,
        filters: activeFilters,
        isResponseReady: isPropertyListReady
    });

    const propertiesAmplitudeItemListName = offer ? getInvestmentApartmentsAmplitudeItemListName({offerType: offer.type}) : null;

    const propertiesAmplitudeItemListContext = useMemo<IAmplitudeItemListContext | null>(
        () =>
            propertiesAmplitudeItemListId && propertiesAmplitudeItemListName
                ? {itemListId: propertiesAmplitudeItemListId, itemListName: propertiesAmplitudeItemListName}
                : null,
        [propertiesAmplitudeItemListId, propertiesAmplitudeItemListName]
    );

    const handleQuickViewOpen = (id: number) => {
        setSelectedIndex(id);
        openQuickViewHit(offerId, propertyList.properties[id].id);
    };

    const handleQuickViewClose = (): void => {
        setSelectedIndex(null);
        closeQuickViewHit();
    };

    const handleListTypeChange = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");

        if (type !== currentPropertyListType) {
            trackAmplitudeButtonClick({
                buttonName: "offers_view_toggle",
                buttonCategory: "listing_map",
                value: type === PropertyListType.TILES ? "floor_plans" : "list",
                itemId: offerId,
                propertyId: null
            });
        }

        setCurrentPropertyListType(type);
    };

    useEffect(() => {
        setPanelListQuery(null);
    }, [offerId]);

    const activeFiltersKey = useMemo(() => JSON.stringify(activeFilters), [activeFilters]);
    const trackedFiltersRef = useRef<{offerId: number; filtersKey: string} | null>(null);

    useEffect(() => {
        if (!offer || !isPropertyListReady || !propertiesAmplitudeItemListId) {
            return;
        }

        const trackedFilters = trackedFiltersRef.current;
        const isSameInvestment = trackedFilters?.offerId === offer.id;

        if (isSameInvestment && trackedFilters?.filtersKey === activeFiltersKey) {
            return;
        }

        trackedFiltersRef.current = {offerId: offer.id, filtersKey: activeFiltersKey};

        trackAmplitudeEvent("listing_filters_applied", {
            filter_trigger: isSameInvestment ? "manual" : "automatic",
            location: "listing_map",
            item_id: offer.id,
            property_type: null,
            budget: null,
            ...getAmplitudePanelFilterValues(activeFilters),
            completion_date: null,
            house_type: null,
            house_additional_areas: null,
            house_storeys: null,
            item_list_id: propertiesAmplitudeItemListId
        });
    }, [offer, isPropertyListReady, propertiesAmplitudeItemListId, activeFilters, activeFiltersKey]);

    const trackedViewItemListKeyRef = useRef<string | null>(null);
    const amplitudeRequestKey = getInvestmentApartmentsAmplitudeRequestKey({offerId, page: activePage, sort: activeSort, filters: activeFilters});

    useEffect(() => {
        if (!offer || !isPropertyListReady || !propertiesAmplitudeItemListId || !propertiesAmplitudeItemListName || properties.length === 0) {
            return;
        }

        const trackedKey = `${amplitudeRequestKey}|${listView}`;

        if (trackedViewItemListKeyRef.current === trackedKey) {
            return;
        }

        trackedViewItemListKeyRef.current = trackedKey;

        trackAmplitudeEvent("view_item_list", {
            item_id: offer.id,
            item_list_id: propertiesAmplitudeItemListId,
            item_list_name: propertiesAmplitudeItemListName,
            location: "listing_map",
            list_view: listView,
            products: properties.map((property, index) => getAmplitudePropertyListItem({property, mainOffer: amplitudePropertyListMainOffer, index}))
        });
    }, [offer, isPropertyListReady, propertiesAmplitudeItemListId, propertiesAmplitudeItemListName, properties, listView, amplitudeRequestKey]);

    useEffect(() => {
        if (offer && isOpen) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1, ...(propertyList.sort.currentSort ? {sort: propertyList.sort.currentSort} : {})},
                searchFiltersToQuery
            )(dispatch);
            propertyList.page.setCurrentPage(1);
        }
    }, [offer, isOpen]);

    return (
        <>
            {!isMobile && (
                <OfferSidebar animation={isOpen} css={mapOfferSidebarStyle}>
                    {isOpen && offer && (
                        <Suspense fallback={<Loader />}>
                            <div className={sidebarContentWrapperStyle}>
                                <OfferSidebarBox
                                    offer={offer}
                                    onOfferSidebarClose={onClose}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                    offerIndex={offerIndex}
                                />
                                <OfferSidebarPropertiesList
                                    offer={offer}
                                    propertyList={propertyList}
                                    propertyListType={currentPropertyListType}
                                    trackingMeta={trackingMeta}
                                    onPropertyListTypeClick={handleListTypeChange}
                                    amplitudeItemListContext={propertiesAmplitudeItemListContext}
                                />
                            </div>
                        </Suspense>
                    )}
                </OfferSidebar>
            )}

            {isMobile && offer && (
                <ExpandablePanel handleClose={onClose} isOpen={isOpen} className={panelStyle}>
                    <Suspense fallback={<Loader />}>
                        {selectedIndex === null && (
                            <MapOfferExpandablePanelOffer
                                offer={offer}
                                handleSetOfferId={handleSetOfferId}
                                latestQuery={latestQuery}
                                mapOfferListQuery={mapOfferListQuery}
                                handleQuickViewOpen={handleQuickViewOpen}
                                propertyListType={currentPropertyListType}
                                onPropertyListTypeClick={handleListTypeChange}
                                amplitudeItemListContext={amplitudeItemListContext}
                                propertiesAmplitudeItemListContext={propertiesAmplitudeItemListContext}
                                onListQueryChange={setPanelListQuery}
                                offerIndex={offerIndex}
                            />
                        )}

                        {selectedIndex !== null && (
                            <MapOfferExpandablePanelQuickView
                                handleQuickViewClose={handleQuickViewClose}
                                propertyList={propertyList}
                                selectedIndex={selectedIndex}
                                setSelectedIndex={setSelectedIndex}
                                propertiesRequestState={requestState}
                                offer={offer}
                                amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            />
                        )}
                    </Suspense>
                </ExpandablePanel>
            )}
        </>
    );
};

const getAmplitudePanelFilterValues = (filters: Record<string, IQueryValue>) => {
    const floorChoices = toAmplitudeFilterOptionValues(filters.floor_choices);
    const houseStoreys = toAmplitudeFilterOptionValues(filters.house_storeys);
    const floor = floorChoices.length ? mapFromFilterOptions(floorChoices, floorChoiceOptions) : mapFromFilterOptions(houseStoreys, houseStoreysOptions);

    return {
        rooms: mapRooms({lower: toAmplitudeFilterNumber(filters.rooms_0), upper: toAmplitudeFilterNumber(filters.rooms_1)}) ?? null,
        area: mapArea({lower: toAmplitudeFilterText(filters.area_0), upper: toAmplitudeFilterText(filters.area_1)}) ?? null,
        floor: floor ?? null
    };
};

const toAmplitudeFilterNumber = (value: IQueryValue): number | "" => (value == null || value === "" ? "" : Number(value));

const toAmplitudeFilterText = (value: IQueryValue): string => (value == null ? "" : String(value));

const toAmplitudeFilterOptionValues = (value: IQueryValue): string[] => {
    if (Array.isArray(value)) {
        return value.map(String);
    }

    return value == null || value === "" ? [] : [String(value)];
};

const panelStyle = css({
    width: "100%",
    padding: 0
});

const mapOfferSidebarStyle = emotionCss`
    @media (min-width: ${getThemeBreakpoint().md}) {
        padding: 0;
        width: max(70rem, 42%);
    }
`;

const sidebarContentWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    padding: 1.5,
    minHeight: "100%"
});
